import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { submitRequest } from 'features/requests/requestsSlice'
import { ISelectValue } from 'constants/globalTypes'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { Input } from 'components/Input/Input'
import { Button } from 'components/ui/Button/Button'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import useModalClose from 'hooks/useModalClose'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import {
  requestTypeOptions,
  canCreateForOthers,
  balanceType,
  leaveDays,
  ROLE_HR,
  ROLE_MANAGER,
  ROLE_SUPERVISOR,
  REQ_ANNUAL,
  REQ_SICK,
  REQ_UNPAID,
  REQ_SHIFT,
  REQ_OVERTIME,
} from '../requestsData'
import { FormStack, Row, BalanceHint } from './NewRequestModal.styled'

const RANGE_TYPES = [REQ_ANNUAL, REQ_SICK, REQ_UNPAID]

export const NewRequestModal = ({ closeModal }: { closeModal: (submitted?: boolean) => void }) => {
  const dispatch = useDispatch<AppDispatch>()
  useModalClose(closeModal)
  const { currentUser, employees, balances } = useSelector((state: RootState) => state.requests)

  const eligible = useMemo(() => {
    if (!canCreateForOthers(currentUser.role)) return employees.filter((e) => e.id === currentUser.id)
    return employees.filter((e) => {
      if (e.id === currentUser.id) return true
      if (currentUser.role === ROLE_HR) return true
      if (
        (currentUser.role === ROLE_MANAGER || currentUser.role === ROLE_SUPERVISOR) &&
        e.department === currentUser.department
      )
        return true
      return false
    })
  }, [employees, currentUser])

  const [forId, setForId] = useState<number>(currentUser.id)
  const [type, setType] = useState<number>(REQ_ANNUAL)
  const [from, setFrom] = useState<Date | null>(null)
  const [to, setTo] = useState<Date | null>(null)
  const [reason, setReason] = useState('')
  const [coverageName, setCoverageName] = useState('')
  const [hours, setHours] = useState('')

  const isRange = RANGE_TYPES.includes(type)
  const bType = balanceType(type)

  const balancePreview = useMemo(() => {
    if (bType === null || !from) return null
    const bal = balances.find((b) => b.employeeId === forId && b.type === bType)
    if (!bal) return null
    const remaining = bal.entitled - bal.used - bal.pending
    const days = leaveDays(from.toISOString(), (isRange ? to || from : from).toISOString())
    return { remaining, days, over: days > remaining }
  }, [bType, from, to, isRange, forId, balances])

  const coverageOptions = employees
    .filter((e) => e.id !== forId)
    .map((e) => ({ value: e.id, label: `${e.name} · ${e.jobTitle}` }))

  const disabled =
    !from ||
    (isRange && !to) ||
    !reason.trim() ||
    (type === REQ_SHIFT && !coverageName) ||
    (type === REQ_OVERTIME && !hours)

  const handleSave = () => {
    if (disabled || !from) return
    const toDate = isRange ? to || from : from
    dispatch(
      submitRequest({
        requesterId: forId,
        createdById: currentUser.id,
        type,
        from: from.toISOString(),
        to: toDate.toISOString(),
        reason: reason.trim(),
        coverageName: type === REQ_SHIFT ? coverageName : null,
        overtimeHours: type === REQ_OVERTIME ? Number(hours) : null,
        submittedAt: new Date().toISOString(),
      }),
    )
    closeModal(true)
  }

  const forOptions = eligible.map((e) => ({
    value: e.id,
    label: e.id === currentUser.id ? `You (${e.name})` : `${e.name} · ${e.jobTitle}`,
  }))

  return (
    <Modal black>
      <Flex column width='100%' gap='1rem'>
        <Flex between width='100%' center style={{ marginBottom: '0.5rem' }}>
          <TitleText weight='bold'>New Request</TitleText>
          <CursorPointer onClick={() => closeModal()}>
            <CloseIcon />
          </CursorPointer>
        </Flex>

        <FormStack>
          <Row>
            <HonestSelectInput
              nomarg
              labelText='For'
              options={forOptions}
              defaultValue={forId}
              onChange={(v: ISelectValue) => setForId(Number(v.value))}
            />
            <HonestSelectInput
              nomarg
              labelText='Type'
              options={requestTypeOptions}
              defaultValue={type}
              onChange={(v: ISelectValue) => setType(Number(v.value))}
            />
          </Row>

          {isRange ? (
            <Row>
              <CalendarInput nomarg labelText='From' minDate={new Date()} defaultValue={from} onChange={(d: Date | null) => setFrom(d)} />
              <CalendarInput nomarg labelText='To' minDate={from || new Date()} defaultValue={to} onChange={(d: Date | null) => setTo(d)} />
            </Row>
          ) : (
            <Row>
              <CalendarInput nomarg labelText='Date' minDate={new Date()} defaultValue={from} onChange={(d: Date | null) => setFrom(d)} />
              {type === REQ_OVERTIME && (
                <Input nomarg type='number' labelText='Extra hours' placeholder='e.g. 4' value={hours} onChange={(e) => setHours(e.target.value)} />
              )}
            </Row>
          )}

          {type === REQ_SHIFT && (
            <HonestSelectInput
              nomarg
              labelText='Coverage person'
              options={coverageOptions}
              onChange={(v: ISelectValue) => setCoverageName(v ? String(v.label).split(' · ')[0] : '')}
            />
          )}

          {balancePreview && (
            <BalanceHint $danger={balancePreview.over}>
              {balancePreview.days} day(s) requested · {balancePreview.remaining} remaining
              {balancePreview.over ? ' — exceeds balance' : ''}
            </BalanceHint>
          )}

          <Input nomarg labelText='Reason' placeholder='Reason for this request…' value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormStack>

        <Button style={{ marginTop: '0.5rem', width: '100%' }} variant='primary' disabled={disabled} onClick={handleSave}>
          Submit request
        </Button>
      </Flex>
    </Modal>
  )
}
